import { FlexProps } from '@chakra-ui/react'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { VFlex } from 'src/components/common/VFlex'
import { InvoiceFormInputs } from '../InvoiceEditForm'
import { BasePaper } from './BasePaper'
import { BillingAmount } from './BillingAmount'
import { ClientPart } from './ClientPart'
import { CompanyPart } from './CompanyPart'
import { InvoiceItems } from './InvoiceItems'
import { Remarks } from './Remarks'
import { SummaryBox } from './SummaryBox'
import { Tax } from './Tax'
import { TransferAccount } from './TransferAccount'

const scale = 0.8
const transform = `scale(${scale})`
type Props = {
  width?: string
  height?: string
} & FlexProps
export const InvoicePreview: React.FC<Props> = ({
  width,
  height,
  ...flexProps
}) => {
  const { watch } = useFormContext<InvoiceFormInputs>()
  const company = watch('company')
  const billing_date = watch('billing_date')
  const invoice_number = watch('invoice_number')
  const items = watch('items')

  const [sealBase64, setSealBase64] = useState<string>()
  const [logoBase64, setLogoBase64] = useState<string>()

  /**
   * fetchメソッドのblobに吐き出す機能とFileReaderのreadAsDataURLを使って
   * AWSにある会社のロゴ・印鑑の画像URLをbase64にエンコードされたDataURLに変換する
   */
  const generateBase64Image = async (
    imageUrl: string | undefined,
    setBase64: (url: string | undefined) => void
  ) => {
    if (!imageUrl) {
      setBase64(undefined)
      return
    }

    const res = await fetch(imageUrl)
    const blob = await res.blob()

    const uri = await new Promise<string | undefined>((resolve) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.addEventListener('load', () => {
        const resultBase64DataUri =
          typeof reader.result === 'string'
            ? reader.result.replace(/text\/html,/, `image/jpg`)
            : undefined
        resolve(resultBase64DataUri)
      })
    })

    if (uri) {
      setBase64(uri)
    }
  }

  useEffect(() => {
    Promise.all([
      generateBase64Image(company.seal_url, setSealBase64),
      generateBase64Image(company.logo_url, setLogoBase64)
    ])
  }, [company.seal_url, company.logo_url])

  return (
    <VFlex
      gap="20px"
      p="10px"
      backgroundColor={'gray.EAE'}
      flexShrink={0}
      h="calc(100vh - 64px )"
      overflowY="scroll"
      w={['auto', '35vw']}
      {...flexProps}
    >
      <BasePaper width={width} height={height} id="content-pdf-1">
        {/* 入力にリアクティブな部分 */}
        <text
          x={470 / scale}
          y={40 / scale}
          transform={transform}
          textAnchor="end"
          fill="black"
          fontSize={'10px'}
        >
          {billing_date ? format(new Date(billing_date), 'yyyy年MM月dd日') : ''}
        </text>
        <text
          x={470 / scale}
          y={52 / scale}
          transform={transform}
          textAnchor="end"
          fill="black"
          fontSize="10px"
        >
          請求番号：{invoice_number}
        </text>
        {/* 請求書の文字 */}
        <path
          d="M222.586 75.244V76.864H228.022V75.244H222.586ZM222.64 70.276V71.914H227.968V70.276H222.64ZM222.586 77.692V79.33H228.022V77.692H222.586ZM221.722 72.706V74.416H228.616V72.706H221.722ZM223.522 80.194V81.904H226.204V83.956H223.522V85.666H228.04V80.194H223.522ZM222.55 80.194V86.386H224.368V80.194H222.55ZM229.048 70.69V72.238H238.228V70.69H229.048ZM229.498 72.886V74.344H237.94V72.886H229.498ZM228.616 75.046V76.594H238.57V75.046H228.616ZM232.54 69.718V76.36H234.592V69.718H232.54ZM229.84 77.278V86.602H231.838V78.808H236.752V77.278H229.84ZM235.564 77.278V84.586C235.564 84.784 235.51 84.856 235.276 84.856C235.042 84.856 234.286 84.874 233.62 84.838C233.854 85.306 234.124 86.044 234.178 86.566C235.348 86.566 236.194 86.548 236.806 86.278C237.4 85.99 237.58 85.522 237.58 84.604V77.278H235.564ZM230.722 79.618V81.022H236.608V79.618H230.722ZM230.722 81.868V83.272H236.608V81.868H230.722ZM242.026 72.436V74.542H258.028V72.436H242.026ZM250.9 74.29L249.19 74.776C250.702 79.42 253.186 83.38 257.002 85.396C257.362 84.766 258.064 83.902 258.586 83.452C254.896 81.796 252.214 78.232 250.9 74.29ZM255.562 74.866C254.662 76.216 253.114 77.89 251.962 78.898L253.528 80.086C254.716 79.114 256.246 77.584 257.47 76.216L255.562 74.866ZM252.214 70.996C253.33 71.554 254.806 72.418 255.49 73.03L256.858 71.5C256.084 70.924 254.572 70.132 253.492 69.628L252.214 70.996ZM242.728 76.27C243.736 77.242 244.942 78.628 245.428 79.546L247.192 78.268C246.652 77.332 245.392 76.036 244.384 75.136L242.728 76.27ZM248.83 69.736V83.956C248.83 84.298 248.704 84.388 248.362 84.406C248.002 84.406 246.868 84.406 245.77 84.352C246.094 85 246.418 86.008 246.526 86.638C248.128 86.638 249.298 86.584 250.054 86.206C250.792 85.846 251.062 85.234 251.062 83.974V69.736H248.83ZM241.45 82.948L242.8 84.928C244.384 84.01 246.382 82.786 248.182 81.616L247.462 79.654C245.302 80.914 242.962 82.21 241.45 82.948ZM263.932 80.806V86.674H266.02V82.156H273.742V86.656H275.92V80.806H263.932ZM265.03 82.822V84.082H274.516V82.822H265.03ZM265.03 84.784V86.152H274.516V84.784H265.03ZM268.63 69.664V79.708H270.808V69.664H268.63ZM263.518 70.798V72.058H273.688V74.884H263.32V76.144H275.83V70.798H263.518ZM261.682 72.706V74.2H277.918V72.706H261.682ZM262.924 76.774V78.088H276.586V76.774H262.924ZM261.646 78.736V80.23H277.9V78.736H261.646Z"
          fill="#0D0D0D"
          transform={`scale(0.9) translate(32 -5)`}
        />
        <ClientPart />
        <BillingAmount />
        <CompanyPart hasLogo={!!logoBase64 || !!sealBase64} />

        {logoBase64 && (
          <image
            x={'425'}
            y="100"
            // crossOrigin="anonymous"
            height={'40px'}
            width={'40px'}
            xlinkHref={logoBase64}
            // objectFit="contain"
          />
        )}
        {sealBase64 && (
          <image
            x={'425'}
            y="150"
            // crossOrigin="anonymous"
            height={'40px'}
            width={'40px'}
            xlinkHref={sealBase64}
            // objectFit="contain"
          />
        )}

        <InvoiceItems page={1} />
        {items.length <= 12 && (
          <>
            <SummaryBox />
            <Tax />
          </>
        )}
        {items.length <= 4 && <Remarks />}
        {items.length <= 7 && <TransferAccount />}
      </BasePaper>
      {items.length > 4 && (
        <BasePaper width={width} height={height} id="content-pdf-2">
          <InvoiceItems page={2} />
          {items.length >= 5 && <Remarks />}
          {items.length >= 8 && <TransferAccount />}
          {items.length >= 13 && (
            <>
              <SummaryBox />
              <Tax />
            </>
          )}
        </BasePaper>
      )}
    </VFlex>
  )
}
